import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
const apiUrl = process.env.REACT_APP_API_URL;

const Access = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    let timeoutId;
    if (message.text) {
      timeoutId = setTimeout(() => {
        setMessage({ text: "", type: "" });
      }, 3000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [message.text]);

  useEffect(() => {
    fetchUsers(searchTerm, currentPage);
  }, [currentPage]);

  const fetchUsers = async (searchTerm = "", page = 1) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("accessToken");
      if (!token) {
        navigate("/");
        return;
      }

      const queryParams = new URLSearchParams({
        isAuditAccess: "true",
        page: page,
        limit: itemsPerPage,
        ...(searchTerm && { searchTerm: searchTerm }),
      });

      const response = await fetch(`${apiUrl}get-users-list?${queryParams}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }
      const responseData = await response.json();

      if (responseData?.status === 200 && responseData?.data?.users) {
        setUsers(responseData.data.users);
        if (responseData.data.totalCount) {
          setTotalPages(Math.ceil(responseData.data.totalCount / itemsPerPage));
        }
      } else {
        throw new Error("Invalid response format from server");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError({ text: err.message, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers(searchTerm, 1);
  };

  const handleClear = () => {
    setSearchTerm("");
    setCurrentPage(1);
    fetchUsers("", 1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRevokeAccess = async (email) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        navigate("/");
        return;
      }

      const response = await fetch(`${apiUrl}revoke-audit-access`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const responseData = await response.json();

      if (response.ok && responseData.status === 200) {
        setMessage({ text: responseData.message, type: "success" });
        await fetchUsers(searchTerm, currentPage);
      } else {
        throw new Error(responseData.message || "Failed to revoke access");
      }
    } catch (err) {
      console.error("Error revoking access:", err);
      setError({ text: err.message, type: "error" });
    }
  };

  const renderPagination = () => {
    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <button
          className="btn btn-outline-secondary px-4 rounded"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>

        <div className="text-center">
          Page {currentPage} of {totalPages}
        </div>

        <button
          className="btn btn-outline-secondary px-4 rounded"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    );
  };

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <>
      {message.text && (
        <div
          className={`alert alert-${
            message.type === "success" ? "success" : "danger"
          }`}
          role="alert"
        >
          {message.text}
        </div>
      )}

      <div className="dashboard-title mb-3">
        <h1>Access</h1>
        <a href="/add-audit-access" className="btn btn-primary">
          Add User
        </a>
      </div>
      <div className="row mb-3 clearfix">
        <div className="col-12 col-lg-12">
          <div className="filters">
            <div className="row gx-2 clearfix">
              <div className="col-12 col-lg-4 col-xl-5">
                <div>
                  <input
                    type="text"
                    className="form-control"
                    id="search"
                    placeholder="Search by Name, Email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-12 col-lg-3 col-xl-2">
                <button
                  type="button"
                  className="btn btn-secondary w-100"
                  onClick={handleSearch}
                >
                  Search
                </button>
              </div>
              <div className="col-12 col-lg-3 col-xl-2">
                <button
                  type="button"
                  className="btn btn-secondary w-100"
                  onClick={handleClear}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <tr key={user.email}>
                <th scope="row">
                  {(currentPage - 1) * itemsPerPage + index + 1}
                </th>
                <td>{user.userName ? user.userName : "Unknown"}</td>
                <td>{user.email}</td>
                <td>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleRevokeAccess(user.email)}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan="4" className="text-center">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {users.length > 0 && renderPagination()}
      </div>
    </>
  );
};

export default Access;
